import moment from "moment";

import { FINANCIAL_YEAR_START_MONTH } from "src/constants/reportRange";
import { isRangeWithinLimit } from "src/utils/report/reportRange.util";

const DATE_FORMAT = "YYYY-MM-DD";

export interface ReportDatePreset {
  id: "last30Days" | "thisMonth" | "lastMonth" | "thisQuarter" | "lastQuarter" | "thisFinancialYear";
  label: string;
  /** `YYYY-MM-DD` */
  fromDate: string;
  /** `YYYY-MM-DD` */
  toDate: string;
}

/**
 * Ready-made report ranges, as local calendar days. The current periods run up to `today` (a report never
 * needs future days); last month and last quarter are the whole previous period. Quarters are calendar quarters,
 * which are also the financial quarters of a year starting in April. Two presets can cover the same
 * dates (in the first month of a quarter "This quarter" is "This month"); both are offered, and the form
 * lights up only one (see `findSelectedPreset`).
 *
 * @param today `YYYY-MM-DD`
 */
export const getReportDatePresets = (today: string): ReportDatePreset[] => {
  const now = moment.utc(today, DATE_FORMAT);
  const format = (date: moment.Moment) => date.format(DATE_FORMAT);
  const lastMonth = now.clone().subtract(1, "month");
  const lastQuarter = now.clone().subtract(1, "quarter");
  const financialYearStart = now.clone().month(FINANCIAL_YEAR_START_MONTH).startOf("month");
  if (financialYearStart.isAfter(now)) financialYearStart.subtract(1, "year");

  return [
    { id: "last30Days", label: "Last 30 days", fromDate: format(now.clone().subtract(29, "days")), toDate: today },
    { id: "thisMonth", label: "This month", fromDate: format(now.clone().startOf("month")), toDate: today },
    {
      id: "lastMonth",
      label: "Last month",
      fromDate: format(lastMonth.clone().startOf("month")),
      toDate: format(lastMonth.clone().endOf("month")),
    },
    { id: "thisQuarter", label: "This quarter", fromDate: format(now.clone().startOf("quarter")), toDate: today },
    {
      id: "lastQuarter",
      label: "Last quarter",
      fromDate: format(lastQuarter.clone().startOf("quarter")),
      toDate: format(lastQuarter.clone().endOf("quarter")),
    },
    { id: "thisFinancialYear", label: "This financial year", fromDate: format(financialYearStart), toDate: today },
  ];
};

/**
 * The one preset to show as selected for the current dates. Presets that cover the same dates all match, so the
 * one the user last clicked wins while it still matches, else the first match; `undefined` when none match.
 */
export const findSelectedPreset = (
  presets: ReportDatePreset[],
  fromDate: string,
  toDate: string,
  lastClickedId?: ReportDatePreset["id"],
): ReportDatePreset | undefined => {
  const matches = presets.filter((preset) => preset.fromDate === fromDate && preset.toDate === toDate);

  return matches.find((preset) => preset.id === lastClickedId) ?? matches[0];
};

/** The presets that fit the plan's report range. A preset that does not fit is left out, not shortened. */
export const getAllowedReportDatePresets = (today: string, maxRangeMonths: number): ReportDatePreset[] =>
  getReportDatePresets(today).filter((preset) => isRangeWithinLimit(preset.fromDate, preset.toDate, maxRangeMonths));
